import * as THREE from 'three';
import { ENEMY_TUTORIAL_DATA } from '../data/enemies.data.js';
import { showEnemyCard } from './ui/enemyCard.js';

const SEEN_ENEMIES_KEY = 'nihil_seen_enemies';
const MAX_LOS_RANGE = 12;
const LOS_HALF_ANGLE_COS = Math.cos((120 * 0.5 * Math.PI) / 180);

function enemyTypeFromEnemy(enemy) {
  if (!enemy) return '';
  if (enemy.isBoss && enemy.phase === 2) return '';
  if (enemy.isBoss) return 'boss';
  const name = String(enemy.constructor?.name || '').toLowerCase();
  if (name.includes('enforcer')) return 'enforcer';
  if (name.includes('zealot')) return 'zealot';
  if (name.includes('minmon')) return 'minmon';
  if (name.includes('zilua')) return 'zilua';
  return 'drone';
}

/**
 * Check whether the player has already seen a given enemy type.
 * @param {string} enemyTypeId
 * @returns {boolean}
 */
export function hasSeenEnemy(enemyTypeId) {
  if (typeof localStorage === 'undefined') return false;
  try {
    const raw = localStorage.getItem(SEEN_ENEMIES_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.includes(enemyTypeId);
  } catch {
    return false;
  }
}

/**
 * Mark an enemy type as seen in persistent storage.
 * @param {string} enemyTypeId
 */
export function markEnemySeen(enemyTypeId) {
  if (typeof localStorage === 'undefined') return;
  try {
    const raw = localStorage.getItem(SEEN_ENEMIES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const list = Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
    if (!list.includes(enemyTypeId)) list.push(enemyTypeId);
    localStorage.setItem(SEEN_ENEMIES_KEY, JSON.stringify(list));
  } catch {
    // localStorage may fail in private mode. Silent fallback by design.
  }
}

/**
 * Create tutorial runtime services for first-encounter cards and LOS checks.
 * @param {{ game: any }} deps
 */
export function createTutorialRuntime({ game }) {
  const raycaster = new THREE.Raycaster();
  const walls = [];
  const wallMaterial = new THREE.MeshBasicMaterial({ visible: false });
  const tempOrigin = new THREE.Vector3();
  const tempDir = new THREE.Vector3();
  let activeEnemyCardId = '';

  const ensureWallMeshes = (wallList = []) => {
    const needed = Array.isArray(wallList) ? wallList.length : 0;
    while (walls.length < needed) {
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(1, 4, 1), wallMaterial);
      mesh.visible = false;
      walls.push(mesh);
    }
    for (let i = 0; i < walls.length; i++) {
      const mesh = walls[i];
      const wall = wallList[i];
      if (!wall) {
        mesh.visible = false;
        continue;
      }
      const sx = Math.max(0.01, wall.maxX - wall.minX);
      const sz = Math.max(0.01, wall.maxZ - wall.minZ);
      mesh.position.set((wall.minX + wall.maxX) * 0.5, 1.2, (wall.minZ + wall.maxZ) * 0.5);
      mesh.scale.set(sx, 2.4, sz);
      mesh.visible = true;
      mesh.updateMatrixWorld(true);
    }
  };

  const setPaused = (paused) => {
    if (typeof game.setPaused === 'function') {
      game.setPaused(paused);
      return;
    }
    if (paused) game.pause?.();
    else game.resume?.();
  };

  const triggerEncounterCard = (enemyTypeId, options = {}) => {
    if (!enemyTypeId || activeEnemyCardId) return false;
    if (!options.alwaysShow && hasSeenEnemy(enemyTypeId)) return false;
    const enemyData = ENEMY_TUTORIAL_DATA[enemyTypeId];
    if (!enemyData) return false;

    activeEnemyCardId = enemyTypeId;
    setPaused(true);
    showEnemyCard(enemyData, () => {
      if (!options.alwaysShow) markEnemySeen(enemyTypeId);
      activeEnemyCardId = '';
      setPaused(false);
    }, {
      alwaysShow: !!options.alwaysShow,
      accentColor: options.accentColor,
      phaseBadge: options.phaseBadge,
    });
    return true;
  };

  const checkFirstEncounters = (player, activeEnemies, roomCollisionMesh) => {
    if (!player?.alive || activeEnemyCardId || game.state !== 'playing') return;
    const forwardX = Number.isFinite(player.aimX) ? player.aimX : 0;
    const forwardZ = Number.isFinite(player.aimZ) ? player.aimZ : 1;
    const forwardLen = Math.hypot(forwardX, forwardZ) || 1;
    const nx = forwardX / forwardLen;
    const nz = forwardZ / forwardLen;
    const wallsList = Array.isArray(roomCollisionMesh) ? roomCollisionMesh : game.room?.walls || [];
    ensureWallMeshes(wallsList);

    for (const enemy of activeEnemies || []) {
      if (!enemy?.alive) continue;
      const enemyTypeId = enemyTypeFromEnemy(enemy);
      if (!enemyTypeId || hasSeenEnemy(enemyTypeId)) continue;

      const dx = enemy.x - player.x;
      const dz = enemy.z - player.z;
      const dist = Math.hypot(dx, dz);
      if (dist <= 0.001 || dist > MAX_LOS_RANGE) continue;
      const dot = (dx / dist) * nx + (dz / dist) * nz;
      if (dot < LOS_HALF_ANGLE_COS) continue;

      tempOrigin.set(player.x, 1.1, player.z);
      tempDir.set(dx, 0, dz).normalize();
      raycaster.set(tempOrigin, tempDir);
      raycaster.far = dist;
      const hit = raycaster.intersectObjects(walls, false)[0];
      if (hit && hit.distance < dist - 0.05) continue;

      triggerEncounterCard(enemyTypeId);
      break;
    }
  };

  const triggerBossPhase2Card = () => {
    return triggerEncounterCard('boss_phase2', {
      alwaysShow: true,
      accentColor: '#ff2200',
      phaseBadge: 'PHASE 2',
    });
  };

  return {
    checkFirstEncounters,
    triggerEncounterCard,
    triggerBossPhase2Card,
  };
}
